import React, { useEffect, useState } from "react";
import { Col, Row } from "react-flexbox-grid/lib/index";
import ReactDiffViewer from "react-diff-viewer";

import TextField from "@material-ui/core/Input";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip
} from "@material-ui/core";
import { JsonEditor } from "react-jsondata-editor";
import PropTypes from "prop-types";
import EditableDiv from "./EditableDiv";

const EditFormLayout = props => {
  const [formDraftText, setFormDraftText] = useState(props.existingFormText);
  const [invalidJSON, setInvalidJSON] = useState(false);
  const [displayPlainText, setDisplayPlainText] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLoadHistoryModal, setShowLoadHistoryModal] = useState(false);
  const [formDraftTextForDiff, setFormDraftTextForDiff] = useState("");
  const [selectedHistoryItem, setSelectedHistoryItem] = useState({});

  useEffect(() => {
    try {
      setFormDraftText(
        JSON.stringify(JSON.parse(props.existingFormText), null, 2)
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log("Received invalid JSON from database");
    }
  }, [props.existingFormText]);

  useEffect(() => {
    try {
      JSON.parse(formDraftText);
      setInvalidJSON(false);
    } catch (e) {
      // invalid JSON means we can't switch to JSON object editor or update the database
      setInvalidJSON(true);
    }
  }, [formDraftText]);

  return (
    <div>
      <Dialog
        open={showConfirmModal}
        scroll="paper"
        fullWidth
        PaperProps={{
          style: {
            maxWidth: "1100px"
          }
        }}
      >
        <DialogTitle>
          <Row>
            <Col md={10}>Confirm Form Update</Col>
            {!props.formEditorUpdated && (
              <Col md={2}>
                <Button onClick={() => setShowConfirmModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    props.onFormEditSaveClick(JSON.parse(formDraftTextForDiff));
                  }}
                >
                  Confirm
                </Button>
              </Col>
            )}
          </Row>
        </DialogTitle>
        <DialogContent>
          {props.formEditorUpdated ? (
            <div>
              <p>EIMS Database successfully updated.</p>
              <Button
                onClick={() => {
                  props.actions.acknowledgeFormContentChanged();
                  setShowConfirmModal(false);
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <div>
              This will save the form to the EIMS database with the following
              changes:
              {invalidJSON ? (
                <div>Invalid JSON syntax - fix before switching</div>
              ) : (
                <ReactDiffViewer
                  oldValue={JSON.stringify(
                    JSON.parse(props.existingFormText),
                    null,
                    2
                  )}
                  newValue={formDraftTextForDiff}
                  showDiffOnly
                  leftTitle="Current Form"
                  rightTitle="Proposed Changes"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {showLoadHistoryModal ? (
        <Dialog
          open={showLoadHistoryModal}
          fullWidth
          scroll="paper"
          PaperProps={{
            style: {
              maxWidth: "800px"
            }
          }}
        >
          <DialogTitle>
            <Row>
              <Col md={9}>Load Previous Form</Col>
              {!props.formEditorUpdated && (
                <Col md={3}>
                  <Button onClick={() => setShowLoadHistoryModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setFormDraftText(selectedHistoryItem.get("text"));
                      props.actions.formContentChanged();
                    }}
                  >
                    Confirm
                  </Button>
                </Col>
              )}
            </Row>
          </DialogTitle>
          <DialogContent>
            <div>
              {props.formEditorUpdated ? (
                <div>
                  <p>Changes applied to editor.</p>
                  <Button
                    onClick={() => {
                      props.actions.acknowledgeFormContentChanged();
                      setShowLoadHistoryModal(false);
                    }}
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <div>
                  <p>
                    This will load the form from:{" "}
                    {selectedHistoryItem.get("revision_dt")}
                  </p>
                  <p>
                    <b>
                      This operation will overwrite any changes in the editor.
                    </b>
                  </p>
                  <p>
                    <b>
                      {`This operation will not update the EIMS database until 'Save & Process Form Changes' confirmed.`}
                    </b>
                  </p>
                  {invalidJSON ? (
                    <div>Invalid JSON syntax - cannot display changes</div>
                  ) : (
                    <ReactDiffViewer
                      oldValue={formDraftTextForDiff}
                      newValue={selectedHistoryItem.get("text")}
                      showDiffOnly
                      leftTitle="Current Form"
                      rightTitle="Proposed Changes"
                    />
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <></>
      )}
      <Card style={{ overflow: "visible" }}>
        <CardHeader title="Edit EIMS Form"> test </CardHeader>
        <CardContent>
          <Row>
            <Col md={6}>
              <TextField
                placeholder="Enter a sample number to modify."
                onChange={props.onSearchChange}
                fullWidth
              ></TextField>
            </Col>
            <Col md={6}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  props.onSearchClick();
                  setFormDraftText(props.existingFormText);
                }}
              >
                Search Forms
              </Button>
              {props.existingFormText && props.existingFormText !== "{}" && (
                <Tooltip
                  title="Invalid JSON syntax - fix before submitting"
                  disableHoverListener={!invalidJSON}
                >
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={invalidJSON}
                      onClick={() => {
                        setFormDraftTextForDiff(
                          JSON.stringify(JSON.parse(formDraftText), null, 2)
                        );
                        setShowConfirmModal(true);
                      }}
                    >
                      Save & Process Form Changes
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Col>
          </Row>
          {props.existingFormText && props.existingFormText !== "{}" && (
            <Card>
              <CardHeader
                title="Current Form JSON"
                action={
                  <div>
                    <Tooltip
                      title="Invalid JSON syntax - fix before switching"
                      disableHoverListener={!invalidJSON}
                    >
                      <div>
                        <input
                          type="checkbox"
                          disabled={invalidJSON}
                          onChange={() => {
                            setDisplayPlainText(!displayPlainText);
                          }}
                        />
                        <span style={{ fontFamily: "Roboto" }}>
                          Edit Plain Text
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                }
              />
              <CardContent>
                <Row>
                  <Col md={9}>
                    {displayPlainText ? (
                      <EditableDiv
                        formText={formDraftText}
                        trackEdits={newText => {
                          try {
                            setFormDraftText(
                              JSON.stringify(JSON.parse(newText), null, 2)
                            );
                          } catch (e) {
                            setFormDraftText(newText);
                          }
                        }}
                      />
                    ) : (
                      <JsonEditor
                        jsonObject={formDraftText}
                        onChange={output => {
                          setFormDraftText(output);
                        }}
                      />
                    )}
                  </Col>
                  <Col md={3}>
                    <Card>
                      <CardHeader title="Form History" />
                      <CardContent>
                        {props.formHistory.map((historyItem, index) => {
                          return (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={index}>
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setFormDraftTextForDiff(formDraftText);
                                  setSelectedHistoryItem(historyItem);
                                  setShowLoadHistoryModal(true);
                                }}
                              >
                                {historyItem.get("revision_dt")}
                              </Button>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  </Col>
                </Row>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

EditFormLayout.propTypes = {
  onSearchClick: PropTypes.func.isRequired,
  existingFormText: PropTypes.string,
  formEditorUpdated: PropTypes.bool.isRequired,

  // eslint-disable-next-line react/forbid-prop-types
  formHistory: PropTypes.object,
  // formHistory: PropTypes.arrayOf(
  //   PropTypes.objectOf(
  //     PropTypes.shape({
  //       revision_dt: PropTypes.string,
  //       text: PropTypes.string
  //     })
  //   )
  // ),
  onSearchChange: PropTypes.func.isRequired,
  onFormEditSaveClick: PropTypes.func.isRequired,
  actions: PropTypes.shape({
    formContentChanged: PropTypes.func.isRequired,
    acknowledgeFormContentChanged: PropTypes.func.isRequired
  }).isRequired
};

EditFormLayout.defaultProps = {
  existingFormText: "{}",
  formHistory: []
};

export default EditFormLayout;
